import React, { useEffect, useState } from 'react'
import { useContext } from 'react'
import { LinkContainer } from 'react-router-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
import { usePackageConfig } from '../../providers/CreatePackageProvider/CreatePackageProvider'
import { useSeasonConfig } from '../../providers/CreateSeasonProvider/CreateSeasonProvider'
import { useDepositModal } from '../../providers/DepositProvider/DepositProvider'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'

import DraftEventsAssistantHost from '../DraftEventsAssistant/DraftEventsAssistantHost'

import { capitalizeString, checkPermission } from '../../utilities/helpers'
import {
  ORGANIZATION_PERMISSION_SLUGS,
  canViewAccountingReport,
  isHensonFundReportEnabled,
} from '@blocktickets/shared-utils'

import { getOrganizationSessionContext } from '../../utilities/api'

import Dropdown from 'react-bootstrap/Dropdown'
import Container from 'react-bootstrap/Container'
import Navbar from 'react-bootstrap/Navbar'
import Nav from 'react-bootstrap/Nav'
import NavDropdown from 'react-bootstrap/NavDropdown'
import Stack from 'react-bootstrap/Stack'
import Form from 'react-bootstrap/Form'

import desktopLogo from '../../assets/creator-panel-logo.svg'

import './navigation.scss'

const env = process.env.REACT_APP_STAGE || 'development'

export default function Navigation({ user }) {
  const { orgPermissions } = useContext(UserContext)
  const navigate = useNavigate()
  const location = useLocation()
  const { handlePackageShow } = usePackageConfig()
  const { handleSeasonShow } = useSeasonConfig()
  const { handleDepositShow } = useDepositModal()

  const [org, setOrg] = useState()
  const [orgOptions, setOrgOptions] = useState([])
  const [canSwitchOrgs, setCanSwitchOrgs] = useState(false)
  const orgPermissionsReady = orgPermissions?.length > 0
  const canViewPayoutsReport =
    orgPermissionsReady &&
    checkPermission(
      AuthService.getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_BALANCE_PAYOUTS
    )
  const isSuperUser = Boolean(
    user?.user?.uuid &&
    org?.members?.some(
      (member) => member?.uuid === user.user.uuid && member?.superAdmin
    )
  )
  const fundraisingEnabled = Boolean(org?.setting?.fundraisingEnabled)
  const foodAndBeverageEnabled = Boolean(org?.setting?.foodAndBeverage)
  const paymentPlansEnabled = Boolean(org?.setting?.paymentPlans)
  const canManageFundraising =
    fundraisingEnabled &&
    (isSuperUser ||
      Boolean(user?.user?.superAdmin) ||
      (orgPermissionsReady &&
        checkPermission(
          AuthService.getPermissions(),
          ORGANIZATION_PERMISSION_SLUGS.MANAGE_FUNDRAISING
        )))
  const canSendPromoEmails =
    isSuperUser ||
    Boolean(user?.user?.superAdmin) ||
    (orgPermissionsReady &&
      checkPermission(
        AuthService.getPermissions(),
        ORGANIZATION_PERMISSION_SLUGS.CONTACT_ATTENDEES
      ))

  useEffect(() => {
    if (user) {
      let stored = localStorage.getItem('org')
      if (stored) {
        const parsed = JSON.parse(stored)
        setOrg(parsed[0])
        setOrgOptions(parsed)
        setCanSwitchOrgs(Array.isArray(parsed) && parsed.length > 1)
      }
    } else {
      setOrg('')
      setOrgOptions([])
      setCanSwitchOrgs(false)
    }
  }, [user])

  useEffect(() => {
    if (!user || !AuthService.isLoggedIn()) return

    let cancelled = false

    ;(async () => {
      try {
        const sc = await getOrganizationSessionContext()
        if (cancelled || !sc?.data?.organizations?.length) return

        const { organizations, isSuperAdminOrganizer } = sc.data
        const savedUuid = localStorage.getItem('activeOrganizationUuid')
        const activeUuid =
          savedUuid && organizations.some((o) => o.uuid === savedUuid)
            ? savedUuid
            : organizations[0]?.uuid

        AuthService.setSuperAdminOrganizerSession(
          Boolean(isSuperAdminOrganizer)
        )
        AuthService.applyOrganizationSession(organizations, activeUuid)
        const next = AuthService.getOrg()
        setOrg(next[0])
        setOrgOptions(next)
        setCanSwitchOrgs(organizations.length > 1)
      } catch {
        /* leave client-side org snapshot */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user])

  const logout = () => {
    AuthService.logoutUser()
    navigate('/')
  }

  const handleOrganizationChange = (e) => {
    const uuid = e.target.value
    if (!uuid || uuid === org?.uuid) return
    AuthService.switchActiveOrganization(uuid)
    window.location.reload()
  }

  const getNameInitials = (user) => {
    if (user?.user)
      return `${user?.user?.firstName.charAt(0)}${user?.user?.lastName.charAt(
        0
      )}`
  }

  const displayName = (user) => {
    if (user?.user)
      return capitalizeString(
        `${user?.user?.firstName} ${user?.user?.lastName}`
      )
  }

  const getRole = (user) => {
    if (user?.user) {
      const member = org?.members.find(
        (member) => member.uuid === user?.user?.uuid
      )
      if (member?.superAdmin) {
        return 'Admin'
      }
    }
  }

  const resetReportingMenuScroll = () => {
    if (typeof document === 'undefined') return

    const reportingMenu = document.querySelector(
      '#reporting-dropdown-toggle ~ .dropdown-menu'
    )

    if (reportingMenu) {
      reportingMenu.scrollTop = 0
    }
  }

  const handleReportingMenuToggle = (isOpen) => {
    if (!isOpen) {
      resetReportingMenuScroll()
    }
  }

  const reportingMenuSections = [
    {
      id: 'analytics',
      title: 'Analytics',
      reports: [
        {
          path: '/attendance-intelligence',
          label: 'Attendance Intelligence',
        },
        {
          path: '/comp-intelligence',
          label: 'Complimentary Intelligence',
        },
        {
          path: '/geographic-sales-intelligence',
          label: 'Geographic Sales Intelligence',
        },
        {
          path: '/pricing-level-performance',
          label: 'Pricing Level Performance',
        },
        {
          path: '/sales-velocity',
          label: 'Sales Velocity',
        },
        {
          path: '/weather-event-analytics',
          label: 'Weather Event Analytics',
        },
      ],
    },
    {
      id: 'attendance-access',
      title: 'Attendance & Access',
      reports: [
        {
          path: '/access-control-summary',
          label: 'Access Control Summary',
        },
        {
          path: '/daily-tickets-count',
          label: 'Daily Ticket Counts',
        },
        {
          path: '/time-stamp-report',
          label: 'Time Stamp Report',
        },
      ],
    },
    {
      id: 'sales-revenue',
      title: 'Sales & Revenue',
      reports: [
        {
          path: '/custom-fee-report',
          label: 'Custom Fee Report',
        },
        {
          path: '/daily-settlement',
          label: 'Daily Settlement Report',
        },
        {
          path: '/sales-attendance',
          label: 'Event Sales & Attendance',
        },
        ...(foodAndBeverageEnabled
          ? [
              {
                path: '/fnb-analytics',
                label: 'F&B Analytics',
              },
              {
                path: '/fnb-eod-report',
                label: 'F&B End of Day Report',
              },
            ]
          : []),
        // {
        //   path: '/revenue-insights',
        //   label: 'Revenue Insights',
        // },
        // {
        //   path: '/insights-dashboard',
        //   label: 'Insights Dashboard',
        // },
        {
          path: '/flex-package-sales-summary',
          label: 'Flex Package Sales Summary',
        },
        ...(isHensonFundReportEnabled(org?.uuid)
          ? [
              {
                path: '/henson-fund-report',
                label: 'Henson Fund Report',
              },
              {
                path: '/summary-by-payment-method',
                label: 'Summary by Payment Method',
              },
            ]
          : []),
        {
          path: '/yoy-difference-report',
          label: 'YOY Difference',
        },
      ],
    },
    {
      id: 'orders-receivables',
      title: 'Orders & Receivables',
      reports: [
        {
          path: '/ar-aging-details',
          label: 'A/R Aging Details Report',
        },
        {
          path: '/ar-aging-summary',
          label: 'A/R Aging Summary Report',
        },
        {
          path: '/buyer-types-report',
          label: 'Buyer Types Report',
        },
        {
          path: '/order-listings',
          label: 'Order Listings',
        },
        {
          path: '/promoter-sales-reports',
          label: 'Promoter Sales Reports',
        },
        env !== 'production'
          ? {
              path: '/report-builder',
              label: 'Report Builder',
            }
          : null,
        {
          path: '/sales-receivables-by-category',
          label: 'Sales & Receivables by Category',
        },
        {
          path: '/season-report',
          label: 'Season Report',
        },
      ].filter(Boolean),
    },
    {
      id: 'finance',
      title: 'Finance',
      reports: [
        canViewAccountingReport({
          organization: org,
          isSuperAdmin: isSuperUser,
        })
          ? {
              path: '/accounting-report',
              label: 'Accounting',
            }
          : null,
        {
          path: '/flex-pack-deferred-revenue',
          label: 'Flex Pack Deferred Revenue',
        },
        canViewPayoutsReport && paymentPlansEnabled
          ? {
              path: '/payment-plans-report',
              label: 'Payment Plans',
            }
          : null,
        canViewPayoutsReport
          ? {
              path: '/payouts-report',
              label: 'Payouts Report',
            }
          : null,
        {
          path: '/deferred-revenue',
          label: 'Season Deferred Revenue',
        },
      ].filter(Boolean),
    },
    {
      id: 'admin',
      title: 'Admin',
      reports: [
        isSuperUser
          ? {
              path: '/client-report',
              label: 'Client Report',
            }
          : null,
      ].filter(Boolean),
    },
  ].filter((section) => section.reports.length > 0)

  if (location.pathname === '/fulfillment') {
    return null
  }

  return (
    <div className="navigation position-sticky" id="navbar">
      <Navbar>
        <Container className="justify-content-start">
          <LinkContainer to="/" id="logo-link">
            <Navbar.Brand>
              <img
                src={desktopLogo}
                alt="Blocktickets Creator Panel"
                width="150"
                height="29"
              />
            </Navbar.Brand>
          </LinkContainer>
          {AuthService.isLoggedIn() && org && (
            <>
              <Nav
                activeKey={window.location.pathname}
                className="py-0"
                as="nav"
              >
                <ul
                  id="main"
                  role="navigation"
                  className="d-flex align-items-center"
                >
                  <li>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="primary"
                        id="create-dropdown-toggle"
                        className="icon-button-md icon-button--white btn-plus"
                        size="md"
                      >
                        Create
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        id="create-dropdown-menu"
                        className="dropdown-menu-lg"
                      >
                        <ul className="dropdown-list">
                          {org?.setting?.accessPass && (
                            <li className="dropdown-list-item">
                              <LinkContainer to="/access-passes">
                                <Dropdown.Item>Access Pass</Dropdown.Item>
                              </LinkContainer>
                            </li>
                          )}
                          <li className="dropdown-list-item">
                            <LinkContainer to="/events/create">
                              <Dropdown.Item>Event</Dropdown.Item>
                            </LinkContainer>
                          </li>
                          <li className="dropdown-list-item">
                            <Dropdown.Item
                              as="button"
                              onClick={handleSeasonShow}
                            >
                              Season
                            </Dropdown.Item>
                          </li>
                          <li className="dropdown-list-item">
                            <Dropdown.Item
                              as="button"
                              onClick={handlePackageShow}
                            >
                              Package
                            </Dropdown.Item>
                          </li>
                          {process.env.REACT_APP_STAGE !== 'production' && (
                            <li className="dropdown-list-item">
                              <Dropdown.Item
                                as="button"
                                onClick={handleDepositShow}
                              >
                                Deposit
                              </Dropdown.Item>
                            </li>
                          )}
                        </ul>
                      </Dropdown.Menu>
                    </Dropdown>
                  </li>
                  <li>
                    <LinkContainer to="/">
                      <Nav.Link>Events</Nav.Link>
                    </LinkContainer>
                  </li>
                  <li>
                    <NavDropdown title="Packages" id="packages-dropdown-toggle">
                      <ul className="dropdown-list">
                        <li className="dropdown-list-item">
                          <LinkContainer to="/assign-inventory">
                            <NavDropdown.Item>
                              Assign Inventory
                            </NavDropdown.Item>
                          </LinkContainer>
                        </li>
                        <li className="dropdown-list-item">
                          <LinkContainer to="/flex-packages">
                            <NavDropdown.Item>Flex Packages</NavDropdown.Item>
                          </LinkContainer>
                        </li>
                        {/* <li className="dropdown-list-item">
                            <LinkContainer to="/group-purchases">
                              <NavDropdown.Item>
                                Groups Tickets
                              </NavDropdown.Item>
                            </LinkContainer>
                          </li> */}
                        <li className="dropdown-list-item">
                          <LinkContainer to="/season-packages">
                            <NavDropdown.Item>Season Packages</NavDropdown.Item>
                          </LinkContainer>
                        </li>
                      </ul>
                    </NavDropdown>
                  </li>
                  <li>
                    <LinkContainer to="/orders">
                      <Nav.Link>Orders</Nav.Link>
                    </LinkContainer>
                  </li>
                  {process.env.REACT_APP_STAGE !== 'production' && (
                    <li>
                      <LinkContainer to="/deposits">
                        <Nav.Link>Deposits</Nav.Link>
                      </LinkContainer>
                    </li>
                  )}
                  <li>
                    <NavDropdown
                      title="Reporting"
                      id="reporting-dropdown-toggle"
                      onToggle={handleReportingMenuToggle}
                    >
                      <ul className="dropdown-list dropdown-list--grouped">
                        {reportingMenuSections.map((section) => (
                          <React.Fragment key={section.id}>
                            <li className="dropdown-list-section-label">
                              {section.title}
                            </li>
                            {section.reports.map((report) => (
                              <li
                                className="dropdown-list-item"
                                key={`${section.id}-${report.path}`}
                              >
                                <LinkContainer to={report.path}>
                                  <NavDropdown.Item>
                                    {report.label}
                                  </NavDropdown.Item>
                                </LinkContainer>
                              </li>
                            ))}
                          </React.Fragment>
                        ))}
                      </ul>
                    </NavDropdown>
                  </li>
                  <li>
                    <NavDropdown
                      title="Marketing"
                      id="marketing-dropdown-toggle"
                    >
                      <ul className="dropdown-list">
                        {canManageFundraising && (
                          <li className="dropdown-list-item">
                            <LinkContainer to="/fundraisers">
                              <NavDropdown.Item>Fundraisers</NavDropdown.Item>
                            </LinkContainer>
                          </li>
                        )}
                        <li className="dropdown-list-item">
                          <LinkContainer to="/promo-codes">
                            <NavDropdown.Item>Promo codes</NavDropdown.Item>
                          </LinkContainer>
                        </li>
                        {canSendPromoEmails && (
                          <li className="dropdown-list-item">
                            <LinkContainer to="/promo-emails">
                              <NavDropdown.Item>Promo emails</NavDropdown.Item>
                            </LinkContainer>
                          </li>
                        )}
                      </ul>
                    </NavDropdown>
                  </li>
                  {/* Hide on production until ready
                                    {window.location.host !== 'admin.blocktickets.xyz' &&
                                        <li>
                                            <LinkContainer to="/attendees">
                                                <Nav.Link>Check-in</Nav.Link>
                                            </LinkContainer>
                                        </li>
                                    } */}
                </ul>
              </Nav>
              <div className="navigation__actions ms-auto">
                <DraftEventsAssistantHost />
                <Dropdown align="end">
                  <Dropdown.Toggle id="profile-dropdown">
                    {getNameInitials(user)}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-lg dropdown-menu-lg">
                    <Stack gap={1} className="mb-3">
                      {canSwitchOrgs ? (
                        <div
                          className="dropdown-org-select"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Form.Select
                            aria-label="Active organization"
                            value={org?.uuid ?? ''}
                            onChange={handleOrganizationChange}
                            size="sm"
                            className="fw-semibold border-secondary shadow-none"
                          >
                            {orgOptions.map((o) => (
                              <option key={o.uuid} value={o.uuid}>
                                {o.name}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      ) : (
                        <h1 className="normal normal-bold">{org?.name}</h1>
                      )}
                      <Stack gap={1}>
                        <p>{displayName(user)}</p>
                        <span className="text-muted caption">
                          {getRole(user)}
                        </span>
                      </Stack>
                    </Stack>
                    <Stack as="ul" gap={2}>
                      {getRole(user) === 'Admin' && (
                        <li>
                          <LinkContainer to="/analytics">
                            <Dropdown.Item>Analytics</Dropdown.Item>
                          </LinkContainer>
                        </li>
                      )}
                      <li>
                        <LinkContainer to="/settings">
                          <Dropdown.Item id="settings">Settings</Dropdown.Item>
                        </LinkContainer>
                      </li>
                      <li>
                        <Dropdown.Item
                          as="button"
                          id="logout"
                          onClick={() => logout()}
                        >
                          Logout
                        </Dropdown.Item>
                      </li>
                    </Stack>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </>
          )}
        </Container>
      </Navbar>
    </div>
  )
}
